import React, { useState, useEffect, useRef } from "react";

import {
  Dialog,
  DialogContent,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Stack,
  TextField,
  MenuItem,
  Checkbox,
  Autocomplete,
  Menu,
  InputAdornment,
  Snackbar,
  Alert,
  Slide,
  useTheme,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { getCorpPcList } from "../../../API/nimbleAPI";
import { COLORS } from "../../../Components/colors";

const columnsDef = [
  { name: "", dataKey: "checkbox", width: 50 }, // Checkbox Column
  { name: "Corporation Name/DBA Name", dataKey: "corpName", width: 200 },
  { name: "Legal Name", dataKey: "legalName", width: 250 },
  { name: "Brand Type", dataKey: "brandType", width: 120 },
  { name: "Service", dataKey: "service", width: 100 },
  { name: "Brand", dataKey: "brand", width: 100 },
  { name: "PMS", dataKey: "pms", width: 100 },
];

const MemoizedRow = React.memo(
  ({ row, columns, isSelected, onSelectRow }) => {
    return (
      <TableRow hover sx={{ "&:hover": { backgroundColor: COLORS.bgHover } }}>
        {columns.map((col, idx) => (
          <TableCell
            key={idx}
            align={col.dataKey === "checkbox" ? "center" : "left"}
            sx={{
              border: "none",
              borderRight: `1px solid ${COLORS.borderFaint}`,
              borderBottom: `1px solid ${COLORS.borderFaint}`,
              fontSize: 12,
              fontWeight: 500,
              color: COLORS.textGrey,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: col.width,
              py: col.dataKey === "checkbox" ? 0 : 1,
              px: col.dataKey === "checkbox" ? 0 : 1,
            }}
          >
            {col.dataKey === "checkbox" ? (
              <Checkbox
                size="small"
                checked={isSelected}
                onChange={() => onSelectRow(row.id)}
              />
            ) : (
              row[col.dataKey]
            )}
          </TableCell>
        ))}
      </TableRow>
    );
  }
);

function SlideTransition(props) {
  return <Slide {...props} direction="left" />;
}




// --- Main Component ---

const MultiCorpPopup = ({ open = true, onClose, onApply, managementGroups = [], currentManagementGroup, onManagementGroupChange, initialSelectedIds }) => {
  const theme = useTheme();

  // Table State
  const [rows, setRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState(initialSelectedIds || []);
  const [searchQuery, setSearchQuery] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const params = new URLSearchParams(window.location.search);
  const corporationIDFromURL = params.get("corporationID");

  const hasInitializedSelection = useRef(Array.isArray(initialSelectedIds) && initialSelectedIds.length > 0);

  useEffect(() => {
    setSearchQuery("");
  }, [currentManagementGroup]);

  useEffect(() => {
    if (open && Array.isArray(initialSelectedIds)) {
      setSelectedIds(initialSelectedIds);
      if (initialSelectedIds.length > 0) {
        hasInitializedSelection.current = true;
      }
    }
  }, [open, initialSelectedIds]);

  useEffect(() => {
  // 🔁 Reset selection state on management group change
  hasInitializedSelection.current = false;
  setSelectedIds([]);
}, [currentManagementGroup]);

  useEffect(() => {
    const fetchData = async () => {
      const groupId = currentManagementGroup?.ValueCode || "ALL";
      const data = await getCorpPcList(groupId);

      if (!data) return;

      const mappedRows = data.map((item) => ({
        id: `${item.PCID}_${item.CorporationID}`, // ✅ UNIQUE ID
        corpId: item.CorporationID,
        pcId: item.PCID,
        corpName: item.CorporationName,
        legalName: item.PCName,
        brandType: item.PropertyType,
        service: item.Service,
        brand: item.Brand,
        pms: item.PMS,
      }));

      setRows(mappedRows);

      // ✅ PRESELECTION LOGIC (RUNS ONCE PER LOAD)
      if (!hasInitializedSelection.current && corporationIDFromURL) {
        hasInitializedSelection.current = true;

        // 🔹 SPECIAL ID → SELECT ALL
        if (corporationIDFromURL === "010000000000000000000000000000001413") {
          setSelectedIds(mappedRows.map((row) => row.id));
        }
        // 🔹 NORMAL → SELECT ONLY MATCHING CORPORATION
        else {
          const preselectedIds = mappedRows
            .filter((row) => row.corpId === corporationIDFromURL)
            .map((row) => row.id);

          setSelectedIds(preselectedIds);
        }
      }
    };

    if (open) {
      fetchData();
    }
  }, [currentManagementGroup, open]);

  const filteredRows = rows.filter((row) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      row.corpName?.toLowerCase().includes(query) ||
      row.legalName?.toLowerCase().includes(query) ||
      row.brandType?.toLowerCase().includes(query) ||
      row.service?.toLowerCase().includes(query) ||
      row.brand?.toLowerCase().includes(query) ||
      row.pms?.toLowerCase().includes(query)
    );
  });

  // --- Selection Logic ---
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(selectedIds);
      filteredRows.forEach((row) => newSelected.add(row.id));
      setSelectedIds(Array.from(newSelected));
    } else {
      const filteredIds = filteredRows.map((row) => row.id);
      setSelectedIds(selectedIds.filter((id) => !filteredIds.includes(id)));
    }
  };

  const handleSelectRow = React.useCallback((id) => {
    setSelectedIds((prevSelectedIds) => {
      if (prevSelectedIds.includes(id)) {
        return prevSelectedIds.filter((item) => item !== id);
      }
      return [...prevSelectedIds, id];
    });
  }, []);

  const handleApply = (isGenerate = false) => {
    if (isGenerate && selectedIds.length === 0) {
      setSnackbarOpen(true);
      return;
    }
    if (onApply) {
      const selectedRows = rows.filter((row) => selectedIds.includes(row.id));
      const corpIds = [...new Set(selectedRows.map((row) => row.corpId))];
      onApply({ pcIds: selectedIds, corpIds, selectedRows }, isGenerate);
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      {/* --- 1. HEADER / TOOLBAR --- */}
      <Toolbar
        sx={{
          backgroundColor: COLORS.bgToolbar,
          borderBottom: `1px solid ${COLORS.border}`,
          minHeight: "auto !important",
          p: 1,
          justifyContent: "space-between",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} maxHeight={"auto"}>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              color: COLORS.textWhite,
              bgcolor: COLORS.iconGrey,
              "&:hover": { bgcolor: COLORS.iconDarkGrey },
              borderRadius: 1,
              p: 0.5,
            }}
          >
            <Close sx={{ fontSize: 12 }} />
          </IconButton>
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: COLORS.textPrimary }}>
            Corporation Selection
          </Typography>
        </Stack>

        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            className="create-budget-btn"
            size="small"
            onClick={() => handleApply(false)}
            sx={{ height: 28, fontSize: "12px", minWidth: "auto", px: 2 }}
          >
            Apply
          </Button>
          <Button
            variant="contained"
            className="create-budget-btn"
            size="small"
            onClick={() => handleApply(true)}
            sx={{ height: 28, fontSize: "12px", minWidth: "auto", px: 2 }}
          >
            Apply & Generate
          </Button>
        </Stack>
      </Toolbar>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ p: 2 }}>

          {/* --- 2. TOP CONTROLS (Management Group, Count, Search) --- */}
          <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="flex-end">
            <Box>
              <Typography sx={{ fontSize: 11, color: COLORS.textSecondary, mb: 0.5, ml: 1, bgcolor: COLORS.bgWhite, width: "fit-content", px: 0.5, position: "relative", top: 8, zIndex: 1 }}>
                Management Groups
              </Typography>
              <Autocomplete
                options={Array.isArray(managementGroups) ? managementGroups : []}
                value={currentManagementGroup}
                disableClearable
                getOptionLabel={(option) => (typeof option === "string" ? option : option.DisplayText || option.ManagementGroup || "")}
                isOptionEqualToValue={(option, value) => option?.ValueCode === value?.ValueCode}
                onChange={(event, newValue) => {
                  if (onManagementGroupChange) onManagementGroupChange(newValue);
                }}
                sx={{ width: 200 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    sx={{ "& .MuiInputBase-root": { height: 35, fontSize: 12, fontWeight: 600 } }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} style={{ fontSize: "12px" }}>
                    {typeof option === "string" ? option : option.DisplayText || option.ManagementGroup}
                  </li>
                )}
              />
            </Box>

            <Box>
              <Typography sx={{ fontSize: 11, color: COLORS.textSecondary, mb: 0.5, ml: 1, bgcolor: COLORS.bgWhite, width: "fit-content", px: 0.5, position: "relative", top: 8, zIndex: 1 }}>
                Corporation
              </Typography>
              <Box sx={{ border: `1px solid ${COLORS.borderDark}`, borderRadius: "4px", padding: "0 10px", height: "35px", display: "flex", alignItems: "center", width: 200, fontSize: 12, fontWeight: 600, fontFamily: theme.typography.fontFamily }}>
                {selectedIds.length === rows.length && rows.length > 0
                  ? "All Corporations"
                  : `${selectedIds.length.toString().padStart(2, "0")} Corporation(s) selected`}
              </Box>
            </Box>

            <Box>
              <Typography sx={{ fontSize: 11, color: COLORS.textSecondary, mb: 0.5, ml: 1, bgcolor: COLORS.bgWhite, width: "fit-content", px: 0.5, position: "relative", top: 8, zIndex: 1 }}>
                Search
              </Typography>
              <TextField
                variant="outlined"
                size="small"
                sx={{ width: 300, "& .MuiInputBase-root": { height: 35, fontSize: 13 } }}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Box>
          </Stack>

          {/* --- 3. SETUP TABLE UI IMPLEMENTATION --- */}
          <TableContainer sx={{ height: "calc(100vh - 250px)", p: 0, border: `1px solid ${COLORS.borderTableContainer}` }}>
            <Table size="small" stickyHeader sx={{ width: '100%' }}>
              <TableHead>
                {/* Header Text Row */}
                <TableRow>
                  {columnsDef.map((col, idx) => (
                    <TableCell
                      key={idx}
                      align={col.dataKey === "checkbox" ? "center" : "left"}
                      sx={{
                        backgroundColor: COLORS.bgHeader,
                        borderRight: "1px solid white",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        fontSize: 12,
                        width: col.width,
                        minWidth: col.width,
                        // fontWeight: 'bold',
                        p: col.dataKey === "checkbox" ? 0 : 1,
                      }}
                    >
                      {col.dataKey === 'checkbox' ? (
                        <Checkbox
                          size="small"
                          checked={filteredRows.length > 0 && filteredRows.every((row) => selectedIds.includes(row.id))}
                          indeterminate={filteredRows.some((row) => selectedIds.includes(row.id)) && !filteredRows.every((row) => selectedIds.includes(row.id))}
                          onChange={handleSelectAll}
                        />
                      ) : (
                        <span title={col.name}>{col.name}</span>
                      )}
                    </TableCell>
                  ))}
                </TableRow>


              </TableHead>

              <TableBody>
                {filteredRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columnsDef.length} align="center" sx={{ fontSize: 14, color: COLORS.textLightGrey, py: 3 }}>
                      No data available
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRows.map((row) => (
                    <MemoizedRow
                      key={row.id}
                      row={row}
                      columns={columnsDef}
                      isSelected={selectedIds.includes(row.id)}
                      onSelectRow={handleSelectRow}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Filter Options Menu (Popover) */}

        </Box>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          TransitionComponent={SlideTransition}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert onClose={() => setSnackbarOpen(false)} severity="error" variant="filled" sx={{ width: "100%", fontSize: "12px" }}>
            Please select at least one corporation
          </Alert>
        </Snackbar>
      </DialogContent>
    </Dialog>
  );
};

export default React.memo(MultiCorpPopup);